<script setup lang="ts">
  import type { PartnerListSummary } from '@api/models';
  import { computed } from 'vue';

  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import PartnerCompletenessCheckLine from '@/components/partners/PartnerCompletenessCheckLine.vue';
  import { PARTNER_INFORMATION_FIELDS } from '@/constants/partnerInformationConstants';
  import { useUsers } from '@/store';

  const props = defineProps<{ partner: PartnerListSummary }>();
  const userStore = useUsers();

  interface HealthLine {
    healthy: boolean;
    label: string;
  }

  const healthLines = computed<HealthLine[]>(() => {
    const cc = props.partner.contact_completeness;
    const assigneeUser = props.partner.assignee_id
      ? userStore.getUserById(props.partner.assignee_id)
      : null;

    return PARTNER_INFORMATION_FIELDS.filter((f) => f.isCompletenessField).map((information) => {
      let isHealthy = false;
      switch (information.value) {
        case 'primary_email':
          isHealthy = cc.has_primary_email && !cc.is_email_disabled;
          break;
        case 'primary_phone':
          isHealthy = cc.has_primary_phone && !cc.is_phone_disabled;
          break;
        case 'assignee':
          isHealthy = props.partner.hasAssignee;
          break;
        case 'assignee_email_linked':
          isHealthy = Boolean(assigneeUser?.default_email_sender_id);
          break;
      }
      return { healthy: isHealthy, label: information.label };
    });
  });
</script>

<template>
  <Tooltip>
    <TooltipTrigger>
      <i
        :class="
          cn(
            'far text-xs text-center',
            healthLines.every((line) => line.healthy)
              ? 'fa-check text-success'
              : 'fa-exclamation-circle text-destructive',
          )
        "
      />
    </TooltipTrigger>

    <TooltipContent class="flex flex-col gap-y-3 py-2">
      <h3 class="font-semibold text-xs underline">Completeness Check</h3>

      <div class="flex flex-col gap-y-4">
        <PartnerCompletenessCheckLine
          v-for="(line, idx) in healthLines"
          :key="idx"
          :healthy="line.healthy"
        >
          {{ line.label }}
        </PartnerCompletenessCheckLine>
      </div>
    </TooltipContent>
  </Tooltip>
</template>

<style scoped></style>
